import {Navigate, RouteObject} from 'react-router-dom';
import {lazyAdminRoute} from '@common/admin/routes/lazy-admin-route';
import {authGuard} from '@common/auth/guards/auth-route';

export const appAdminRoutes: RouteObject[] = [
  // Reports
  {
    path: 'insights',
    loader: () => authGuard({permission: 'insights.streaming'}),
    lazy: () => lazyAdminRoute('BemusicAdminReportPage'),
    children: [
      {index: true, lazy: () => lazyAdminRoute('AdminInsightsReport')},
      {path: 'plays', lazy: () => lazyAdminRoute('AdminInsightsReport')},
      {path: 'visitors', lazy: () => lazyAdminRoute('AdminVisitorsReport')},
    ],
  },
  // Channels
  {
    path: 'channels',
    lazy: () => lazyAdminRoute('ChannelsDatatablePage'),
  },
  {
    path: 'focus-items',
    loader: () => authGuard({permission: 'focus.view'}),
    lazy: () => lazyAdminRoute('FocusItemsDatatablePage'),
  },
  {
    path: 'newsletter-campaigns',
    loader: () => authGuard({permission: 'newsletter_campaigns.view'}),
    lazy: () => lazyAdminRoute('NewsletterCampaignsDatatablePage'),
  },
  {
    path: 'ad-slots',
    loader: () => authGuard({permission: 'ads.view'}),
    lazy: () => lazyAdminRoute('AdsDatatablePage'),
  },
  {
    path: 'album-taxonomies',
    loader: () => authGuard({permission: 'taxonomies_types.view'}),
    lazy: () => lazyAdminRoute('AlbumTaxonomiesDatatablePage'),
  },
  {
    path: 'album-taxonomies/:typeId/edit',
    loader: () => authGuard({permission: 'taxonomies_types.update'}),
    lazy: () => lazyAdminRoute('UpdateAlbumTypePage'),
  },
  {
    path: 'newsletter-campaigns/new',
    loader: () => authGuard({permission: 'newsletter_campaigns.create'}),
    lazy: () => lazyAdminRoute('CreateNewsletterCampaignPage'),
  },
  {
    path: 'newsletter-campaigns/:campaignId/edit',
    loader: () => authGuard({permission: 'newsletter_campaigns.update'}),
    lazy: () => lazyAdminRoute('UpdateNewsletterCampaignPage'),
  },
  {
    path: 'newsletter-campaign-runs/:runId',
    loader: () => authGuard({permission: 'newsletter_campaigns.view'}),
    lazy: () => lazyAdminRoute('NewsletterCampaignRunDetailsPage'),
  },
  {
    path: 'channels/new',
    lazy: () => lazyAdminRoute('CreateChannelPage'),
  },
  {
    path: 'channels/:slugOrId/edit',
    lazy: () => lazyAdminRoute('EditChannelPage'),
  },
  // Tracks
  {
    path: 'tracks',
    lazy: () => lazyAdminRoute('TracksDatatablePage'),
  },
  {
    path: 'tracks/new',
    lazy: () => lazyAdminRoute('CreateTrackPage'),
  },
  {
    path: 'tracks/:trackId/edit',
    lazy: () => lazyAdminRoute('UpdateTrackPage'),
  },
  {
    path: 'tracks/:trackId/insights',
    lazy: () => lazyAdminRoute('NestedBackstageTrackInsights'),
  },
  // Albums
  {
    path: 'albums',
    lazy: () => lazyAdminRoute('AlbumsDatatablePage'),
  },
  {
    path: 'albums/new',
    lazy: () => lazyAdminRoute('CreateAlbumPage'),
  },
  {
    path: 'albums/:albumId/edit',
    lazy: () => lazyAdminRoute('UpdateAlbumPage'),
  },
  {
    path: 'albums/:albumId/insights',
    lazy: () => lazyAdminRoute('NestedBackstageAlbumInsights'),
  },
  // Artists
  {
    path: 'artists',
    lazy: () => lazyAdminRoute('ArtistDatatablePage'),
  },
  {
    path: 'artists/new',
    lazy: () => lazyAdminRoute('CreateArtistPageWithExternalFields'),
  },
  {
    path: 'artists/:artistId/edit',
    lazy: () => lazyAdminRoute('UpdateArtistPageWithExternalFields'),
  },
  {
    path: 'artists/:artistId/insights',
    lazy: () => lazyAdminRoute('NestedBackstageArtistInsights'),
  },
  {
    path: 'owners',
    loader: () => authGuard({permission: 'owners.view'}),
    lazy: () => lazyAdminRoute('OwnersDatatablePage'),
  },
  // Upload
  {
    path: 'upload',
    lazy: () => lazyAdminRoute('UploadPage'),
  },
  // Backstage requests
  {
    path: 'backstage-requests',
    lazy: () => lazyAdminRoute('BackstageRequestsDatatablePage'),
  },
  {
    path: 'backstage-requests/:requestId',
    lazy: () => lazyAdminRoute('ViewBackstageRequestPage'),
  },
  // Fraud flags
  {
    path: 'fraud-flags',
    loader: () => authGuard({permission: 'fraud.view'}),
    lazy: () => lazyAdminRoute('FraudFlagsDatatablePage'),
  },
  {
    path: 'fraud-flags/:flagId',
    loader: () => authGuard({permission: 'fraud.view'}),
    lazy: () => lazyAdminRoute('FraudFlagDetailsPage'),
  },
  // Royalties
  {
    path: 'royalties',
    loader: () => authGuard({permission: 'royalties.view'}),
    lazy: () => lazyAdminRoute('RoyaltiesAdminPage'),
    children: [
      {index: true, element: <Navigate to="runs" replace />},
      {
        path: 'runs',
        lazy: () => lazyAdminRoute('RoyaltiesRunsPage'),
      },
      {
        path: 'snapshot',
        lazy: () => lazyAdminRoute('RoyaltiesSnapshotPage'),
      },
      {
        path: 'runs/:runId',
        lazy: () => lazyAdminRoute('RoyaltiesRunDetailsPage'),
      },
      {
        path: 'settings',
        lazy: () => lazyAdminRoute('RoyaltiesSettingsPage'),
      },
      {
        path: 'settings/new',
        loader: () => authGuard({permission: 'royalties.create'}),
        lazy: () => lazyAdminRoute('CreateRoyaltiesSettingPage'),
      },
      {
        path: 'settings/:settingId/edit',
        loader: () => authGuard({permission: 'royalties.update'}),
        lazy: () => lazyAdminRoute('UpdateRoyaltiesSettingPage'),
      },
    ],
  },
  {
    path: 'popularity',
    loader: () => authGuard({permission: 'settings.update'}),
    lazy: () => lazyAdminRoute('PopularityAdminPage'),
    children: [
      {index: true, element: <Navigate to="observability" replace />},
      {
        path: 'observability',
        lazy: () => lazyAdminRoute('PopularityObservabilityPage'),
      },
      {
        path: 'factors',
        lazy: () => lazyAdminRoute('ArtistAdjustmentFactorsPage'),
      },
    ],
  },
  // Genres
  {
    path: 'genres',
    lazy: () => lazyAdminRoute('GenresDatatablePage'),
  },
  // Playlists
  {
    path: 'playlists',
    lazy: () => lazyAdminRoute('PlaylistDatatablePage'),
  },
  {
    path: 'playlist-generators',
    loader: () => authGuard({permission: 'playlists.automatic'}),
    lazy: () => lazyAdminRoute('PlaylistGeneratorsDatatablePage'),
  },
  // Lyrics
  {
    path: 'lyrics',
    lazy: () => lazyAdminRoute('LyricsDatatablePage'),
  },
  // Comments
  {
    path: 'comments',
    lazy: () => lazyAdminRoute('CommentsDatatablePage'),
  },
  
  // Live Events
  {
    path: 'live-events',
    loader: () => authGuard({permission: 'live_event.view', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventsPageDataTable'),
  },
  {
    path: 'live-events/new',
    loader: () => authGuard({permission: 'live_event.create', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventPageCreateAdmin'),
  },
  {
    path: 'live-events/:slugOrId/edit',
    loader: () => authGuard({permission: 'live_event.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventPageUpdateAdmin'),
  },
  {
    path: 'live-events/:slugOrId/access',
    loader: () => authGuard({permission: 'live_event.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventAccessPageAdmin'),
  },
  {
    path: 'live-events/:slugOrId/tickets',
    loader: () => authGuard({permission: 'live_event.tickets.view', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventsTicketsDataTablePage'),
  },
  {
    path: 'live-events/:slugOrId/tickets/new',
    loader: () => authGuard({permission: 'live_event.tickets.create', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventTicketPageCreateAdmin'),
  },
  {
    path: 'live-events/:slugOrId/tickets/:ticketId/edit',
    loader: () => authGuard({permission: 'live_event.tickets.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventTicketPageUpdateAdmin'),
  },
  {
    path: 'live-events/:slugOrId/scan-sessions',
    loader: () => authGuard({permission: 'live_event.scan_sess.view', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventsScanSessionDataTablePage'),
  },
  {
    path: 'live-events/:slugOrId/scan-sessions/new',
    loader: () => authGuard({permission: 'live_event.scan_sess.create', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventScanSessionPageCreateAdmin'),
  },
  {
    path: 'live-events/:slugOrId/scan-sessions/:sessionId/edit',
    loader: () => authGuard({permission: 'live_event.scan_sess.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveEventScanSessionPageUpdateAdmin'),
  },
  
  // Live Vote
  {
    path: 'live-votes',
    loader: () => authGuard({permission: 'live_vote.view', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveVotesPageDataTable'),
  },
  {
    path: 'live-votes/new',
    loader: () => authGuard({permission: 'live_vote.create', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveVotePageCreate'),
  },
  {
    path: 'live-votes/:slugOrId/edit',
    loader: () => authGuard({permission: 'live_vote.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveVotePageUpdateAdmin'),
  },
  {
    path: 'live-votes/:slugOrId/server',
    loader: () => authGuard({permission: 'live_vote.update', redirectUrl: "/"}),
    lazy: () => lazyAdminRoute('LiveVoteServerPage'),
  },
];
